import React, { useState } from 'react';
import _ from 'underscore';
import SyntaxHighlighter from 'react-syntax-highlighter';
import { a11yLight } from 'react-syntax-highlighter/dist/esm/styles/hljs';

import { getConfig } from '../../../../functions/getConfig';
import { Languages } from '../../../../util/appConstants';
import {
  ActiveTabBox,
  Heading,
  HeadingUnderLine,
  FlexContainer,
  CapsuledTab,
  LeftCapsuledTab,
  RightCapsuledTab,
  InnerBorderPrintContainer,
  BorderPrintContainer,
} from '../styles';
import {
  TestCodeButton,
  CodeWrittenText,
  CodeContainer,
  FlexParent,
  InputFlexContainer,
  Timeline,
  TimelineDot,
  InputBestRun,
  DescriptionText,
  BoldText,
  OvertimeTextDescription,
} from './styles';

export default React.memo(({ codeInputs, ticket, bestRunInput }) => {
  const config = getConfig();
  const [selectedInput, setSelectedInput] = useState(bestRunInput);
  const [code, setCode] = useState(bestRunInput.code);
  const [showPastedTimeline, setShowPastedTimeline] = useState(false);
  const [inputs, setInputs] = useState(codeInputs);

  const msToMinutesAndSeconds = (milliseconds) => {
    var minutes = Math.floor(milliseconds / 60000);
    var seconds = ((milliseconds % 60000) / 1000).toFixed(0);
    return seconds === 60 ? minutes + 1 + ':00' : minutes + ':' + (seconds < 10 ? '0' : '') + seconds;
  };

  const getTimeOfRun = (input) => {
    var runTime = new Date(input.timeOfRequest || input.timeOfPaste || input.updatedAt);
    var startTime = new Date(ticket.startedAt);
    return msToMinutesAndSeconds(runTime - startTime);
  };

  const getScore = (input) => {
    let maxScore = _.reduce(
      input.levelMaxScores,
      function (memo, num) {
        return memo + (num ? num : 0);
      },
      0
    );

    let userScore = _.reduce(
      input.levelScores,
      function (memo, num) {
        return memo + (num ? num : 0);
      },
      0
    );

    return input.levelScores ? userScore + ' / ' + maxScore : 'No score available';
  };

  const scrollToCode = () => {
    let element = document.getElementById(`code`);
    let headerOffset = 60;
    let elementPosition = element.getBoundingClientRect().top;
    let offsetPosition = elementPosition + window.pageYOffset - headerOffset;
    window.scrollTo({
      top: offsetPosition,
      behavior: 'smooth',
    });
  };

  const renderTimeline = () => {
    // Used to show which dots are hight lighted as active, inActive, and beforeActive on the timeline
    let activeInputIndex = _.findIndex(inputs, (input) => input._id === selectedInput._id);
    return inputs.map((input, i) => {
      return (
        <Timeline
          key={input._id}
          numDots={inputs.length > 10 ? 22 : inputs.length * 2}
          finalSubmit={(input.isFinalSubmit && !ticket.isOvertime) || input.isBestAfterDeadline}
          lastPastedInput={showPastedTimeline && inputs.length - 1 === i} // Do not show the line to the right of the last pasted dot
          isLongPastedTimeline={showPastedTimeline && inputs.length > 15}
          active={i === activeInputIndex}
          inactive={i > activeInputIndex}
          beforeActive={i < activeInputIndex}
          onClick={() => {
            setSelectedInput(input);
            setCode(input.code);
            scrollToCode();
          }}
        >
          <TimelineDot timeOfRun={getTimeOfRun(input)} scoreOfRun={showPastedTimeline ? '' : getScore(input)}>
            {input.isBestRun && !showPastedTimeline && <InputBestRun>BEST RUN</InputBestRun>}
            {input.isBestAfterDeadline && !showPastedTimeline && <InputBestRun>OVERTIME</InputBestRun>}
          </TimelineDot>
        </Timeline>
      );
    });
  };

  return (
    <ActiveTabBox forPrint={true} forCode={true}>
      <BorderPrintContainer>
        <InnerBorderPrintContainer>
          <Heading>CODE</Heading>
          <HeadingUnderLine />
          <FlexContainer>
            <CodeWrittenText id="code">
              Code written in {selectedInput?.languageVersion || Languages[selectedInput?.language]?.SimpleName}
            </CodeWrittenText>
            {!showPastedTimeline && (
              <TestCodeButton
                href={`${config.REDIRECTION_URL}/freecode-show-code/${ticket._id}/${selectedInput._id}`}
                target="_blank"
              >
                <i className="fa fa-edit">{` `}Test Code</i>
              </TestCodeButton>
            )}
          </FlexContainer>
          <CodeContainer>
            <SyntaxHighlighter
              // The library require uses a different name for c# then we do in editorNames
              language={selectedInput.language === 'c_sharp' ? 'csharp' : selectedInput.language}
              style={a11yLight}
            >
              {code}
            </SyntaxHighlighter>
          </CodeContainer>
          {showPastedTimeline && (
            <>
              <CodeWrittenText>Pasted Text</CodeWrittenText>
              <CodeContainer>
                <SyntaxHighlighter
                  language={selectedInput.language === 'c_sharp' ? 'csharp' : selectedInput.language}
                  style={a11yLight}
                >
                  {selectedInput.pastedText}
                </SyntaxHighlighter>
              </CodeContainer>
            </>
          )}
          <FlexContainer marginTop={'20px'} handleSmallDevice>
            <Heading>{showPastedTimeline ? 'PASTED TEXTS TIMELINE' : 'TIMELINE'}</Heading>
            {ticket?.pastedTextsInputs?.length > 0 && (
              <CapsuledTab>
                <LeftCapsuledTab
                  active={!showPastedTimeline}
                  onClick={() => {
                    setShowPastedTimeline(false);
                    setInputs(codeInputs);
                    setSelectedInput(bestRunInput);
                    setCode(bestRunInput.code);
                  }}
                >
                  Timeline
                </LeftCapsuledTab>
                <RightCapsuledTab
                  active={showPastedTimeline}
                  onClick={() => {
                    setShowPastedTimeline(true);
                    setInputs(ticket.pastedTextsInputs);
                    setSelectedInput(ticket.pastedTextsInputs[0]);
                    setCode(selectedInput.code);
                  }}
                >
                  Pasted Texts Timeline
                </RightCapsuledTab>
              </CapsuledTab>
            )}
          </FlexContainer>
          <FlexParent isLongPastedTimeline={showPastedTimeline && inputs.length > 15}>
            <InputFlexContainer isLongPastedTimeline={showPastedTimeline && inputs.length > 15}>
              {renderTimeline()}
            </InputFlexContainer>
          </FlexParent>
          <>
            <DescriptionText>
              Each dot represents a point in time during the test, showing the elapsed time (mm:ss) below and the score
              above. The dot furthest to the right represents the submitted code
              {ticket.bestRun ? ' and the best scoring code is marked with "BEST RUN"' : ''}.
              <BoldText> Click on a dot to display the code at that point!</BoldText>
              {ticket.isOvertime && (
                <OvertimeTextDescription>
                  <i className="fa fa-info-circle"></i>
                  <BoldText className="overtime-text prompt">&nbsp;Overtime</BoldText> - The official result is the one
                  achieved within the time limit
                </OvertimeTextDescription>
              )}
            </DescriptionText>
          </>
        </InnerBorderPrintContainer>
      </BorderPrintContainer>
    </ActiveTabBox>
  );
});
